import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Lock, Clock, FileCheck2, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export function ExamsList() {
  const [exams, setExams] = useState([]);
  const [history, setHistory] = useState([]);
  useEffect(() => {
    api.get("/exams").then(r => setExams(r.data.exams));
    api.get("/exams/history/me").then(r => setHistory(r.data.history)).catch(() => {});
  }, []);

  return (
    <div className="space-y-5" data-testid="exams-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Mock Exams</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1">Timed, scored, with detailed explanations.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {exams.map(e => (
          <div key={e.exam_id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 card-hover">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="pill bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{e.category}</div>
                <h3 className="mt-2 text-lg font-semibold">{e.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">{e.description}</p>
              </div>
              {e.locked && <Lock className="h-4 w-4 text-amber-500 flex-shrink-0" />}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {e.duration_min} min</span>
              <span>Pass: {e.pass_percentage}%</span>
            </div>
            {e.locked ? (
              <Link to="/subscription" data-testid={`exam-locked-${e.exam_id}`} className="btn-outline mt-4 w-full">Unlock Premium</Link>
            ) : (
              <Link to={`/exams/run/${e.exam_id}`} data-testid={`start-exam-${e.exam_id}`} className="btn-primary mt-4 w-full">Start exam</Link>
            )}
          </div>
        ))}
      </div>

      {history.length > 0 && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          <h3 className="text-lg font-semibold mb-3">Your history</h3>
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {history.map(h => (
              <li key={h.attempt_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">{h.exam_title}</div>
                  <div className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <div className={`mono font-bold ${h.passed ? "text-emerald-500" : "text-rose-500"}`}>{h.percentage}%</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function ExamRunner() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [start, setStart] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/exams/${examId}`).then(r => {
      setExam(r.data.exam);
      setRemaining(r.data.exam.duration_min * 60);
      setStart(Date.now());
    }).catch(() => toast.error("Cannot start exam"));
  }, [examId]);

  useEffect(() => {
    if (!exam) return;
    const t = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [exam]);

  useEffect(() => { if (exam && remaining === 0) submit(); /* eslint-disable-next-line */ }, [remaining]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const payload = {
      exam_id: examId,
      time_taken_sec: Math.floor((Date.now() - start) / 1000),
      answers: exam.questions.map(q => ({ question_id: q.question_id, answer: answers[q.question_id] ?? -1 })),
    };
    try {
      const { data } = await api.post("/exams/submit", payload);
      nav(`/exams/result/${data.attempt_id}`);
    } catch (e) {
      toast.error("Submit failed");
      setSubmitting(false);
    }
  };

  if (!exam) return <div className="py-10 text-center text-slate-500">Loading exam…</div>;
  const q = exam.questions[idx];
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className="max-w-3xl mx-auto" data-testid="exam-runner">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{exam.title}</div>
          <div className="text-sm font-semibold">Question {idx + 1} of {exam.questions.length}</div>
        </div>
        <div className={`mono font-bold text-xl ${remaining < 60 ? "text-rose-500" : ""}`} data-testid="exam-timer">{mm}:{ss}</div>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div className="pill bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">{q.difficulty}</div>
        <h2 className="mt-3 text-xl font-semibold">{q.text}</h2>
        <div className="mt-5 space-y-2">
          {q.options.map((opt, i) => (
            <button key={i} onClick={() => setAnswers(a => ({ ...a, [q.question_id]: i }))}
                    data-testid={`exam-option-${i}`}
                    className={`w-full text-left rounded-xl border p-3 transition-colors ${answers[q.question_id] === i ? "border-amber-500 bg-amber-500/10" : "border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
              <span className="mono text-xs mr-2">{String.fromCharCode(65 + i)}</span>{opt}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2">
          <button data-testid="exam-prev" disabled={idx === 0} onClick={() => setIdx(i => Math.max(0, i - 1))} className="btn-outline disabled:opacity-40">Previous</button>
          {idx < exam.questions.length - 1 ? (
            <button data-testid="exam-next" onClick={() => setIdx(i => i + 1)} className="btn-primary">Next</button>
          ) : (
            <button data-testid="exam-submit" onClick={submit} disabled={submitting} className="btn-accent">{submitting ? "Submitting…" : "Submit exam"}</button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {exam.questions.map((qq, i) => (
            <button key={qq.question_id} onClick={()=>setIdx(i)}
                    className={`h-8 w-8 rounded-lg text-xs font-semibold ${i === idx ? "bg-slate-900 text-white dark:bg-amber-500 dark:text-slate-950" : answers[qq.question_id] !== undefined ? "bg-emerald-500/20 text-emerald-600" : "bg-slate-100 dark:bg-slate-800"}`}>{i+1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ExamResult() {
  const { attemptId } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/exams/attempt/${attemptId}`).then(r => setA(r.data.attempt)); }, [attemptId]);
  if (!a) return <div className="py-10 text-center text-slate-500">Loading…</div>;

  const wrong = a.details.filter(d => !d.is_correct);
  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="exam-result">
      <Link to="/exams" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to exams</Link>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-6 bg-white dark:bg-slate-900 text-center">
        <div className="text-xs uppercase tracking-widest text-slate-500">Result</div>
        <div className={`mt-2 text-6xl font-extrabold mono ${a.passed ? "text-emerald-500" : "text-rose-500"}`}>{a.percentage}%</div>
        <div className="mt-1 font-semibold">{a.exam_title}</div>
        <div className="mt-3 pill mx-auto inline-flex" style={{background: a.passed ? "rgba(16,185,129,0.15)" : "rgba(244,63,94,0.15)", color: a.passed ? "#10b981" : "#f43f5e"}}>{a.passed ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{a.passed ? "Passed" : "Failed"}</div>
        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
          <div><div className="text-slate-500">Correct</div><div className="mono font-bold">{a.score}/{a.total}</div></div>
          <div><div className="text-slate-500">Time</div><div className="mono font-bold">{Math.floor((a.time_taken_sec||0)/60)}m {(a.time_taken_sec||0)%60}s</div></div>
          <div><div className="text-slate-500">Wrong</div><div className="mono font-bold">{wrong.length}</div></div>
        </div>
      </div>

      <div className="space-y-3">
        {a.details.map((d, i) => (
          <div key={i} className={`rounded-2xl border p-4 ${d.is_correct ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500">Q{i+1}</div>
            <div className="font-semibold">{d.text}</div>
            <div className="mt-2 text-sm">
              {d.options.map((o, j) => (
                <div key={j} className={`py-1 ${j === d.correct_answer ? "font-semibold text-emerald-600" : ""} ${j === d.user_answer && j !== d.correct_answer ? "text-rose-600 line-through" : ""}`}>
                  {String.fromCharCode(65+j)}. {o}
                </div>
              ))}
            </div>
            {d.explanation && <div className="mt-2 text-sm text-slate-600 dark:text-slate-300"><span className="font-semibold">Explanation:</span> {d.explanation}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ExamsList;
